import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "../auth/supabaseClient";
import type { LobbyPlayer, LobbyChatMessage } from "./types";

// ── Presence payload ────────────────────────────────────────────────────────

interface PresencePayload {
  userId: string;
  displayName: string;
  elo: number;
  ready: boolean;
  equippedSkin: string;
  level: number;
}

// ── Broadcast event types ───────────────────────────────────────────────────

interface ChatBroadcast {
  userId: string;
  displayName: string;
  message: string;
  ts: number;
}

interface ReadyBroadcast {
  userId: string;
  ready: boolean;
}

interface LobbyStateBroadcast {
  state: string;
  matchId?: string;
  seed?: number;
}

interface RematchBroadcast {
  userId: string;
  wants: boolean;
}

interface RematchLeaveBroadcast {
  userId: string;
}

// ── Callbacks ───────────────────────────────────────────────────────────────

export interface LobbyRealtimeCallbacks {
  /** Called when the player list changes (join/leave/ready update). */
  onPlayersChanged: (players: LobbyPlayer[]) => void;
  /** Called when a chat message is received. */
  onChatMessage: (msg: LobbyChatMessage) => void;
  /** Called when lobby state changes (e.g. COUNTDOWN, MATCH_STARTING). */
  onLobbyStateChanged: (state: string, matchId?: string, seed?: number) => void;
  /** Called when opponent signals rematch intent. */
  onRematchIntent?: (userId: string, wants: boolean) => void;
  /** Called when opponent leaves the result screen. */
  onRematchLeave?: (userId: string) => void;
}

// ── Lobby Realtime Manager ──────────────────────────────────────────────────

export class LobbyRealtime {
  private channel: RealtimeChannel | null = null;
  private localUserId: string | null = null;
  private callbacks: LobbyRealtimeCallbacks | null = null;
  private chatIdCounter = 0;

  /**
   * Subscribe to a lobby's realtime channel.
   * Tracks presence (players) and listens for broadcast events (chat, ready, state).
   */
  subscribe(
    lobbyId: string,
    localUserId: string,
    localDisplayName: string,
    localElo: number,
    callbacks: LobbyRealtimeCallbacks,
    equippedSkin = "1",
    level = 1
  ): void {
    // Unsubscribe from any previous channel
    this.unsubscribe();

    this.localUserId = localUserId;
    this.callbacks = callbacks;

    const supabase = getSupabase();
    const channel = supabase.channel(`bb:lobby:${lobbyId}`, {
      config: { presence: { key: localUserId } }
    });

    // ── Presence ────────────────────────────────────────────────────────
    channel.on("presence", { event: "sync" }, () => {
      this.handlePresenceSync(channel);
    });

    // ── Broadcast: chat ─────────────────────────────────────────────────
    channel.on("broadcast", { event: "chat" }, ({ payload }) => {
      const data = payload as ChatBroadcast;
      this.callbacks?.onChatMessage({
        id: ++this.chatIdCounter,
        userId: data.userId,
        displayName: data.displayName,
        message: data.message,
        createdAt: data.ts
      });
    });

    // ── Broadcast: ready toggle ─────────────────────────────────────────
    // The sender's trackReady() already updates their presence, which
    // triggers a presence sync on all clients. This broadcast is just a
    // fast-path hint — we don't need to re-track (doing so would corrupt
    // the local user's own presence entry).
    channel.on("broadcast", { event: "ready" }, () => {
      // Presence sync handles the player list update automatically.
    });

    // ── Broadcast: lobby state change ───────────────────────────────────
    channel.on("broadcast", { event: "lobby_state" }, ({ payload }) => {
      const data = payload as LobbyStateBroadcast;
      this.callbacks?.onLobbyStateChanged(data.state, data.matchId, data.seed);
    });

    // ── Broadcast: rematch intent ───────────────────────────────────────
    channel.on("broadcast", { event: "rematch" }, ({ payload }) => {
      const data = payload as RematchBroadcast;
      if (data.userId !== this.localUserId) {
        this.callbacks?.onRematchIntent?.(data.userId, data.wants);
      }
    });

    // ── Broadcast: rematch leave ────────────────────────────────────────
    channel.on("broadcast", { event: "rematch_leave" }, ({ payload }) => {
      const data = payload as RematchLeaveBroadcast;
      if (data.userId !== this.localUserId) {
        this.callbacks?.onRematchLeave?.(data.userId);
      }
    });

    // Subscribe and track our own presence
    void channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await channel.track({
          userId: localUserId,
          displayName: localDisplayName,
          elo: localElo,
          ready: false,
          equippedSkin,
          level
        } as PresencePayload);
      }
    });

    this.channel = channel;
  }

  /** Unsubscribe from the lobby channel and clean up. */
  unsubscribe(): void {
    if (this.channel != null) {
      void this.channel.unsubscribe();
      const supabase = getSupabase();
      void supabase.removeChannel(this.channel);
      this.channel = null;
    }
    this.localUserId = null;
    this.callbacks = null;
    this.chatIdCounter = 0;
  }

  /** Broadcast a chat message to the lobby. */
  sendChat(displayName: string, message: string): void {
    if (this.channel == null || this.localUserId == null) return;
    void this.channel.send({
      type: "broadcast",
      event: "chat",
      payload: {
        userId: this.localUserId,
        displayName,
        message,
        ts: Date.now()
      } as ChatBroadcast
    });
  }

  /** Update our ready state in presence and broadcast to others. */
  async trackReady(ready: boolean, displayName: string, elo: number, equippedSkin = "1", level = 1): Promise<void> {
    if (this.channel == null || this.localUserId == null) return;
    await this.channel.track({
      userId: this.localUserId,
      displayName,
      elo,
      ready,
      equippedSkin,
      level
    } as PresencePayload);
    // Also broadcast so others update immediately
    void this.channel.send({
      type: "broadcast",
      event: "ready",
      payload: { userId: this.localUserId, ready } as ReadyBroadcast
    });
  }

  /** Broadcast a lobby state change (host only). */
  broadcastLobbyState(state: string, matchId?: string, seed?: number): void {
    if (this.channel == null) return;
    void this.channel.send({
      type: "broadcast",
      event: "lobby_state",
      payload: { state, matchId, seed } as LobbyStateBroadcast
    });
  }

  /** Broadcast rematch intent to the lobby channel. */
  broadcastRematch(wants: boolean): void {
    if (this.channel == null || this.localUserId == null) return;
    void this.channel.send({
      type: "broadcast",
      event: "rematch",
      payload: { userId: this.localUserId, wants } as RematchBroadcast
    });
  }

  /** Broadcast that we are leaving the result screen (no rematch). */
  broadcastRematchLeave(): void {
    if (this.channel == null || this.localUserId == null) return;
    void this.channel.send({
      type: "broadcast",
      event: "rematch_leave",
      payload: { userId: this.localUserId } as RematchLeaveBroadcast
    });
  }

  isConnected(): boolean {
    return this.channel != null;
  }

  // ── Internal ────────────────────────────────────────────────────────────

  private handlePresenceSync(channel: RealtimeChannel): void {
    const state = channel.presenceState<PresencePayload>();
    const players: LobbyPlayer[] = [];

    for (const presences of Object.values(state)) {
      // Each key may have multiple presences; take the latest
      const latest = presences[presences.length - 1];
      if (latest != null) {
        players.push({
          userId: latest.userId,
          displayName: latest.displayName,
          avatarUrl: null,
          ready: latest.ready,
          elo: latest.elo,
          equippedSkin: latest.equippedSkin ?? "1",
          level: latest.level ?? 1
        });
      }
    }

    this.callbacks?.onPlayersChanged(players);
  }
}
